import React, { useState } from 'react';
import { Download, Share2, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface DesignCardProps {
  imageUrl: string;
  title: string;
  category: string;
  likes?: number;
}

const DesignCard: React.FC<DesignCardProps> = ({ 
  imageUrl, 
  title, 
  category,
  likes = Math.floor(Math.random() * 500) + 50
}) => {
  const { t } = useLanguage();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'success' | 'error'>('idle');
  
  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setDownloadStatus('downloading');
      
      // Fetch the image
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      
      // Convert to blob
      const imageBlob = await response.blob();
      
      // Create object URL
      const blobUrl = URL.createObjectURL(imageBlob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${title.toLowerCase().replace(/\s+/g, '-')}.jpg`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      setDownloadStatus('success');
      
      // Reset status after 2 seconds
      setTimeout(() => {
        setDownloadStatus('idle');
      }, 2000);
      
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('error');
      
      // Reset error status after 3 seconds
      setTimeout(() => {
        setDownloadStatus('idle');
      }, 3000);
    } finally {
      setIsDownloading(false);
    }
  };
  
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: `Check out this ${category} design: ${title}`,
        url: window.location.href,
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  
  const getDownloadButtonContent = () => {
    switch (downloadStatus) {
      case 'downloading':
        return <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />;
      case 'success':
        return <span className="text-green-500">✓</span>;
      case 'error':
        return <span className="text-red-500">×</span>;
      default:
        return <Download className="h-5 w-5" />;
    }
  };
  
  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden group transition-all duration-300 hover:shadow-xl hover:shadow-purple-900/20 border border-gray-700">
      <div className="relative aspect-square overflow-hidden">
        <img 
          src={imageUrl} 
          alt={title} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
          <div>
            <p className="text-gray-300 text-sm font-medium">{category}</p>
            <h3 className="text-white font-semibold">{title}</h3>
          </div>
          <div className="flex items-center gap-1">
            <Heart className="h-4 w-4 text-pink-500" />
            <span className="text-white text-sm">{likes}</span>
          </div>
        </div>
      </div>
      <div className="p-4 flex justify-between items-center">
        <span className="text-gray-400 text-sm">{category}</span>
        <div className="flex gap-3">
          <button 
            className="text-gray-400 hover:text-pink-500 transition-colors p-2 rounded-full hover:bg-gray-700"
            aria-label={t('designCard.download')}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {getDownloadButtonContent()}
          </button>
          <button 
            className="text-gray-400 hover:text-purple-500 transition-colors"
            aria-label={t('designCard.share')}
            onClick={handleShare}
          >
            <Share2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesignCard;